import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography
} from "@mui/material";
import { Visibility, Delete } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { useSnackbar } from "notistack";
import Loading from "../../components/Loading";
import ViewResource from "./ViewResource";

export default function AllResource() {
  const lang = Cookies.get("i18next") || "en";
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [resource, setResource] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [open, setOpen] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    async function getAdminResources() {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_KEY}api/v1/admin/all-resources`,
          { signal }
        );

        if (!response.ok) throw new Error(`Error: ${response.status}`);

        const data = await response.json();
        setResource(data.data || []);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Failed to fetch admin resources:", err);
          setError("Failed to fetch resources");
        }
      } finally {
        setLoading(false);
      }
    }

    getAdminResources();
    return () => controller.abort();
  }, []);

  const handleViewClose = () => setOpen(false);

  const handleDeleteClick = (res) => {
    if (!res.isVerified) {
      enqueueSnackbar(t("Cannot delete a resource under review."), {
        variant: "warning",
      });
      return;
    }
    setResourceToDelete(res);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_KEY}api/v1/resources/delete-resource/${resourceToDelete.id}`,
        { method: "DELETE" }
      );

      if (!response.ok) throw new Error("Delete failed");

      setResource((prev) =>
        prev.filter((res) => res.id !== resourceToDelete.id)
      );

      enqueueSnackbar(t("Resource deleted successfully"), {
        variant: "success",
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(t("Failed to delete resource"), { variant: "error" });
    } finally {
      setDeleteDialogOpen(false);
      setResourceToDelete(null);
    }
  };

  const columns = [
    { id: "#", label: t("#"), minWidth: 100 },
    { id: "Type of Resource", label: t("Type of Resource"), minWidth: 150 },
    { id: "price", label: t("price"), minWidth: 100 },
    { id: "status", label: t("status"), minWidth: 100 },
    { id: "actions", label: t("actions"), minWidth: 150 },
  ];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(+e.target.value);
    setPage(0);
  };

  return (
    <>
      {!isLoading ? (
        <Paper sx={{ width: "100%", padding: "20px" }}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    align="center"
                    style={{ minWidth: col.minWidth }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
              <TableBody>
                {resource
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow hover key={row.id}>
                      <TableCell align="center">{row.id}</TableCell>
                      <TableCell align="center">{t(row.resource)}</TableCell>
                      <TableCell align="center">{row.price}</TableCell>
                      <TableCell align="center">
                        {row.isVerified ? t("Available") : t("Review")}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          color="warning"
                          onClick={() => setOpen(row.id)}
                          sx={{ marginRight: "10px" }}
                        >
                          <Visibility />
                        </Button>
                        <Dialog open={open === row.id} onClose={handleViewClose}>
                          <ViewResource
                            setResource={setResource}
                            resource={row}
                            handleClose={handleViewClose}
                          />
                        </Dialog>
                        <Button
                          color="error"
                          onClick={() => handleDeleteClick(row)}
                        >
                          <Delete />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={resource.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      ) : (
        <Loading />
      )}

      {/* Dialog for confirm delete */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("Confirm Deletion")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("Are you sure you want to delete this resource?")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            {t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
